<template>
  <el-dialog
    v-model="isVisible"
    class="modal-overlay"
    width="30%"
    @close="close"
  >
    <div id="paypal-container" />
    <div id="googlepay-container"></div>
    <div id="applepay-container"></div>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import { loadScript, PayPalNamespace } from '@paypal/paypal-js'
import { captureOrder } from '@/api/paypal_order'
import { IPaypalOrderData, PaymentMethod } from '@/interfaces/paypal_order'
import { ElMessage } from 'element-plus'
import { IReplenishmentResponseMeta } from '@/interfaces/replenishments'
import appEnv from '@/support/env'

const currency = 'EUR'

export default defineComponent({
  name: 'PaypalModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    orderData: {
      type: Object as () => IReplenishmentResponseMeta,
      default: () => ({})
    }
  },
  setup(props, context) {
    const isVisible = ref(props.visible)

    let paypalInitialized = false
    let paypal: PayPalNamespace | null
    let applePay: any = null
    let googlePayInitialized = false
    let googlePaymentsClient: google.payments.api.PaymentsClient
    let googlePaymentConfig: google.payments.api.PaymentDataRequest
    let appleSession: undefined | null

    // @ts-ignore
    const baseCardPaymentMethod: google.payments.api.PaymentMethodSpecification =
      {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          allowedCardNetworks: ['MASTERCARD', 'DISCOVER', 'VISA', 'AMEX'],
          billingAddressRequired: true,
          billingAddressParameters: {
            format: 'FULL',
            phoneNumberRequired: false
          }
        }
      }

    async function onGooglePaymentButtonClicked() {
      try {
        const paymentDataRequest = await getPaymentDataRequest()
        await googlePaymentsClient.loadPaymentData(paymentDataRequest)
      } catch (err) {
        console.error('Payment failed or cancelled:', err)
      }
    }

    function addGooglePayButton() {
      const button = googlePaymentsClient.createButton({
        onClick: onGooglePaymentButtonClicked,
        allowedPaymentMethods: [baseCardPaymentMethod],
        buttonColor: 'black',
        buttonType: 'long'
      })
      // @ts-ignore: Object is possibly 'null'
      document.getElementById('googlepay-container').appendChild(button)
    }

    async function getGoogleConfig(): Promise<google.payments.api.PaymentDataRequest> {
      if (!paypal) {
        throw new Error(
          'Unable to provide google config object, PayPal skd not loaded yet'
        )
      }
      // @ts-ignore
      return await paypal.Googlepay().config()
    }

    async function onGooglePayLoaded() {
      googlePayInitialized = true
      googlePaymentConfig = await getGoogleConfig()

      googlePaymentsClient = new google.payments.api.PaymentsClient({
        environment: appEnv.PAYPAL_ENV,
        merchantInfo: googlePaymentConfig.merchantInfo,
        paymentDataCallbacks: {
          onPaymentAuthorized: paymentAuthorized
        }
      })

      const request = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: googlePaymentConfig.allowedPaymentMethods
      }
      googlePaymentsClient
        .isReadyToPay(request)
        .then(function (response) {
          if (response.result) {
            addGooglePayButton()
          }
        })
        .catch(function (error) {
          console.error(
            `Unable to check isReadyToPay, something went wrong. ${error}`
          )
        })
    }

    async function getPaymentDataRequest() {
      return {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: googlePaymentConfig.allowedPaymentMethods,
        merchantInfo: googlePaymentConfig.merchantInfo,
        callbackIntents: ['PAYMENT_AUTHORIZATION'],
        transactionInfo: buildTransactionInfo(),
        shippingAddressRequired: true
      } as google.payments.api.PaymentDataRequest
    }

    function buildTransactionInfo() {
      const data = props.orderData
      return {
        totalPriceStatus: 'FINAL',
        totalPrice: data.amount,
        currencyCode: data.currency,
        // @ts-ignore
        countryCode: googlePaymentConfig.countryCode
      } as google.payments.api.TransactionInfo
    }

    function paymentAuthorized(
      paymentData: google.payments.api.PaymentData
    ): Promise<google.payments.api.PaymentAuthorizationResult> {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async function (resolve, reject) {
        try {
          // @ts-ignore
          const confirmOrderResponse = await paypal.Googlepay().confirmOrder({
            orderId: props.orderData.order_id,
            paymentMethodData: paymentData.paymentMethodData
          })

          if (confirmOrderResponse.status === 'APPROVED') {
            const payload = {
              order_id: props.orderData.order_id,
              payment_method: PaymentMethod.GooglePay
            } as IPaypalOrderData

            await captureOrder(payload).then((response) => {
              close()
              if (response.status) {
                successMessage()
                resolve({ transactionState: 'SUCCESS' })
              } else {
                failMessage()
                resolve({
                  transactionState: 'ERROR',
                  error: {
                    reason: 'OTHER_ERROR',
                    intent: 'PAYMENT_AUTHORIZATION',
                    message: 'Unable to process transaction'
                  }
                })
              }
            })
          } else {
            resolve({
              transactionState: 'ERROR',
              error: {
                reason: 'OTHER_ERROR',
                intent: 'PAYMENT_AUTHORIZATION',
                message: 'TRANSACTION FAILED'
              }
            })
          }
        } catch (err) {
          resolve({
            transactionState: 'ERROR',
            error: {
              reason: 'OTHER_ERROR',
              intent: 'PAYMENT_AUTHORIZATION',
              // @ts-ignore
              message: err.message
            }
          })
        }
      })
    }

    async function initApplePay() {
      const script = document.createElement('script')
      script.src =
        'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js'
      script.async = true
      script.onload = () => {
        setupApplePayButton()
      }
      script.onerror = () => {
        console.error('Unable to load Apple Pay script')
      }
      document.head.appendChild(script)
    }

    async function setupApplePayButton() {
      if (!paypal) {
        return
      }

      if (!paypal.Applepay) {
        console.error('PayPal Apple Pay SDK not loaded. Something went wrong.')
        return
      }

      if (!window.ApplePaySession) {
        console.error('Apple Pay not supported on this device')
        return
      }

      if (!window.ApplePaySession.canMakePayments()) {
        console.error('Apple Pay not available for this user')
        return
      }

      applePay = paypal.Applepay()

      if (!applePay) {
        console.error('Unable to build Apple Pay provider')
        return
      }

      const config = await applePay.config()

      if (!config.isEligible) {
        console.warn('Merchant or device not eligible for Apple Pay via PayPal')
        return
      }

      const container = document.getElementById('applepay-container')
      if (!container) throw new Error('Apple Pay container not found')

      container.innerHTML = `<apple-pay-button id="btn-apple" buttonstyle="black" type="buy" locale="en"></apple-pay-button>`

      const btn = document.getElementById('btn-apple')
      if (btn) {
        btn.addEventListener('click', () => startApplePayFlow(config, applePay))
      }
    }

    async function startApplePayFlow(config: any, applepay: any) {
      const data = props.orderData
      const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
        countryCode: config.countryCode,
        currencyCode: data.currency,
        merchantCapabilities: config.merchantCapabilities,
        supportedNetworks: config.supportedNetworks,
        requiredBillingContactFields: ['postalAddress', 'name'],
        total: {
          label: 'Traforama',
          amount: data.amount,
          type: 'final'
        }
      }
      appleSession = new window.ApplePaySession!(4, paymentRequest)

      appleSession.onvalidatemerchant = async (event: any) => {
        try {
          const response = await applepay.validateMerchant({
            validationUrl: event.validationURL,
            displayName: 'Traforama'
          })

          const session = response.merchantSession || response
          appleSession.completeMerchantValidation(session)
        } catch (err) {
          console.error('validateMerchant error:', err)
          appleSession.abort()

          ElMessage({
            message: err.message,
            duration: 10000,
            type: 'error'
          })
        }
      }
      appleSession.onpaymentauthorized = onApplePaymentAuthorized
      appleSession.oncancel = () => {}
      appleSession.begin()
    }

    async function onApplePaymentAuthorized(event: any) {
      try {
        // @ts-ignore
        const confirmResp = await applePay.confirmOrder({
          orderId: props.orderData.order_id,
          token: event.payment.token,
          billingContact: event.payment.billingContact
        })

        const payload = {
          order_id: props.orderData.order_id,
          payment_method: PaymentMethod.ApplePay
        } as IPaypalOrderData

        const captureResponse = await captureOrder(payload)

        if (captureResponse.status) {
          appleSession.completePayment(window.ApplePaySession!.STATUS_SUCCESS)
          close()
          successMessage()
        } else {
          appleSession.completePayment(window.ApplePaySession!.STATUS_FAILURE)
          failMessage()
        }
      } catch (err) {
        console.error('[onApplePaymentAuthorized] error:', err)
        appleSession.completePayment(window.ApplePaySession!.STATUS_FAILURE)
        failMessage()
      }
    }

    async function initGooglePay() {
      const script = document.createElement('script')
      script.src = 'https://pay.google.com/gp/p/js/pay.js'
      script.async = true
      script.onload = onGooglePayLoaded
      document.head.appendChild(script)
    }

    async function initPaypalPayments() {
      try {
        paypal = await loadScript({
          clientId: appEnv.PAYPAL_CLIENT_ID,
          merchantId: appEnv.PAYPAL_MERCHANT_ID,
          intent: 'authorize',
          currency: currency,
          components: ['buttons', 'googlepay', 'applepay'],
          enableFunding: ['card']
        })

        if (paypal) {
          paypalInitialized = true

          // @ts-ignore: Object is possibly 'null'
          await paypal
            .Buttons({
              // @ts-ignore
              createOrder: function (_data, _actions) {
                return Promise.resolve(props.orderData.order_id)
              },
              onApprove: async function (data, _actions) {
                const payload = {
                  order_id: data.orderID,
                  payment_method: PaymentMethod.PayPal
                } as IPaypalOrderData

                return captureOrder(payload).then((response) => {
                  close()
                  if (response.status) {
                    successMessage()
                  } else {
                    failMessage()
                  }
                })
              },
              onCancel: function (data) {
                ElMessage.error(
                  'Unable to complete payment, canceled by PayPal'
                )
              },
              onError: function (err) {
                ElMessage.error(
                  'Unable to process payment, please check your payment details or try another method'
                )
              }
            })
            .render('#paypal-container')
        }
      } catch (error) {
        console.error('Failed to load PayPal SDK', error)
      }
    }

    const close = () => {
      context.emit('close')
    }

    watch(
      (_: any) => props.visible,
      async (val: any) => {
        isVisible.value = val

        if (val) {
          if (!paypalInitialized) {
            await initPaypalPayments()
          }
          if (!googlePayInitialized) {
            await initGooglePay()
          }
          await initApplePay()
        }
      }
    )

    function successMessage() {
      ElMessage({
        message:
          'Payment completed successfully. It may take awhile for the payment to appear in your account',
        duration: 5000,
        type: 'success'
      })
    }

    function failMessage() {
      ElMessage({
        message:
          'Unable to complete payment, please check your payment details or try another method',
        duration: 5000,
        type: 'error'
      })
    }

    return {
      isVisible,
      close
    }
  }
})
</script>

<style lang="scss" scoped>
#googlepay-container {
  text-align: center;
}
#applepay-container {
  text-align: center;
}
#paypal-container {
  margin-bottom: 8px;
}
</style>
